/**
 * Pull to Refresh Component
 * 
 * Wraps content with pull-to-refresh functionality for mobile/native apps
 */

import React from 'react';
import { RefreshCw } from 'lucide-react';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { cn } from '@/lib/utils';

interface PullToRefreshProps {
  onRefresh: () => Promise<void> | void;
  children: React.ReactNode;
  className?: string;
  threshold?: number;
  resistance?: number;
  enabled?: boolean;
  showIndicator?: boolean;
}

export function PullToRefresh({
  onRefresh,
  children,
  className,
  threshold = 80,
  resistance = 2.5,
  enabled = true,
  showIndicator = true
}: PullToRefreshProps) {
  const {
    containerRef,
    isPulling,
    pullDistance,
    isRefreshing,
    canRefresh,
    getPullIndicatorStyle
  } = usePullToRefresh({
    onRefresh,
    threshold,
    resistance,
    enabled
  });

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative overflow-auto h-full',
        className
      )}
      style={{
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}
    >
      {/* Pull to refresh indicator */}
      {showIndicator && (isPulling || isRefreshing) && (
        <div
          className="absolute top-0 left-0 right-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-sm border-b"
          style={{
            height: `${Math.min(pullDistance, threshold)}px`,
            ...getPullIndicatorStyle()
          }}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw 
              className={cn(
                'h-4 w-4',
                (isRefreshing || canRefresh) && 'animate-spin'
              )} 
            />
            <span>
              {isRefreshing 
                ? 'Syncing...' 
                : canRefresh 
                  ? 'Release to sync' 
                  : 'Pull down to sync'
              }
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div
        className={cn(
          'transition-transform duration-300 ease-out',
          isPulling && 'transform'
        )}
        style={{
          transform: isPulling ? `translateY(${Math.min(pullDistance, threshold)}px)` : 'translateY(0)',
          transition: isPulling ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
}


